import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Upload, MoveLeft } from 'lucide-react';
import { logoColor } from '../../ui/media';
import { useStore } from '@tanstack/react-store';
import { themeStore } from '../../../stores/theme';
import { useRegister, useRegisterOTP, useVerifyOtp } from '../../../hooks/useAuth';
import PhoneInput from 'react-phone-input-2';
import toast from 'react-hot-toast';
import AddressAutocomplete from '../../common/AddressAutoComplete'
import 'react-phone-input-2/lib/style.css';

// Main App Component
const Register = () => {
  const { isDarkMode } = useStore(themeStore)
  const RegisterMutation = useRegister()
  const RegisterOtpMutation = useRegisterOTP()
  const navigate = useNavigate()
  const [email, setEmail] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [password, setPassword] = useState('');
  const [confPassword, setConfPassword] = useState('');
  const [companyProfile, setCompanyProfile] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [countryCode, setCountryCode] = useState('+1')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [taxId, setTaxId] = useState('');
  const [loadOtpScreen, setLoadOtpScreen] = useState(false);
  const [companyDocument, setCompanyDocument] = useState(null); // optional
  const [userType, setUserType] = useState(2);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfPassword, setShowConfPassword] = useState(false);
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const inputsRef = useRef([]);
  const verifyOtpMutation = useVerifyOtp()
  const formDataRef = useRef(null);
  const [selectedAddress, setSelectedAddress] = useState({
    coordinates: [],
    type: "point",
    name: ""
  });

  const userTypes = [{
    name: 'EVENT ORGANIZER',
    value: 2
  }, {
    name: 'SECURITY',
    value: 4
  }]

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCompanyProfile(file); // store file
      const imageURL = URL.createObjectURL(file);
      setImagePreview(imageURL);
    }
  };

  const handleRegister = async () => {
    if (companyName.trim() == "") {
      toast.error("Company Name is not allowed to empty")
      return
    }
    if (email.trim() == "") {
      toast.error("Company Email is not allowed to empty")
      return
    }
    if (password.trim() == "" || confPassword.trim() == "") {
      toast.error("Password is not allowed to empty")
      return
    }
    if (password.trim() !== confPassword.trim()) {
      toast.error("Password and Confirm Password should be matched")
      return
    }
    if (countryCode.trim() == "") {
      toast.error("Please Select Valid Country Code")
      return
    }
    if (phoneNumber.trim() == "") {
      toast.error("Phone Number is not allowed to empty")
      return
    }
    if (selectedAddress?.name == "") {
      toast.error("Please Select Valid Address")
      return
    }
    if (!companyDocument) {
      toast.error("Share Holder ID must be required")
      return
    }
    const formData = new FormData();
    formData.append("name", companyName);
    formData.append("email", email);
    formData.append("phone_number", phoneNumber);
    formData.append("address", JSON.stringify(selectedAddress));
    formData.append("country_code", countryCode);
    formData.append("user_type", userType);
    formData.append("password", password);
    formData.append("company_document", companyDocument);
    formData.append("taxId_number", taxId);
    if (companyProfile) {
      formData.append("company_logo", companyProfile);
    }
    formDataRef.current = formData;
    try {
      RegisterOtpMutation.mutate({
        email, name: companyName
      }, {
        onSuccess: () => {
          setLoadOtpScreen(true)
        },
        onError: (err) => {
          const message = err.response?.data?.message || "Register OTP Send failed"
          toast.error(message)
        }
      })
    } catch (error) {
      const message = error.response?.data?.message || "Register OTP Send error"
      toast.error(message)
    }
  };

  const handlePhoneChange = (value, data) => {
    setPhoneNumber(value.slice(data.dialCode.length)) // only the number after country code
    setCountryCode(data.dialCode) // country code as number, e.g. '1'
  }

  const Logo = () => {
    return (
      <div className="flex flex-col items-center">
        <img src={logoColor} className='h-[70px]' />
      </div>
    );
  };

  const handleChange = (element, index) => {
    const value = element.value.replace(/[^0-9]/g, '');
    if (value) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (index < 5) {
        inputsRef.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        inputsRef.current[index - 1].focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1].focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleSubmit = () => {
    const enteredOtp = otp.join('');
    if (enteredOtp?.length < 6) {
      toast.error("OTP should be of 6 Digits")
      return
    }
    verifyOtpMutation.mutate({ email, otp: enteredOtp }, {
      onSuccess: () => {
        // call registration mutation
        RegisterMutation.mutate(formDataRef.current, {
          onSuccess() {
            navigate('/waiting-for-review')
          },
          onError(err) {
            const message = err.response?.data?.message || "Register failed"
            toast.error(message)
          }
        })
      },
      onError: (error) => {
        const message = error.response?.data?.message || "Server Error"
        toast.error(message)
      }
    })
  };

  const resendOtp = () => {
    RegisterOtpMutation.mutate({
      email, name: companyName
    }, {
      onSuccess: () => {
        setOtp(new Array(6).fill(''))
        inputsRef.current[0].focus();
        toast.success("OTP Resent Successfully.")
      },
      onError: (err) => {
        const message = err.response?.data?.message || "Register OTP Resend failed"
        toast.error(message)
      }
    })
  }

  return (
    <>
      <div className={`min-h-screen flex items-center justify-center p-4`}>
        <div className={`w-full max-w-md rounded-lg shadow-lg mt-[70px] overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          {/* Logo Header */}
          <div className="bg-blue-100 w-[95%] mx-auto my-3 rounded-lg p-6 flex justify-center">
            <div className="flex flex-col items-center">
              <Logo />
            </div>
          </div>
          {loadOtpScreen ?
            <>
              <MoveLeft
                onClick={() => setLoadOtpScreen(false)}
                className='absolute ml-6 mt-2 cursor-pointer' />
              <h1 className={`text-2xl poppins-regular font-bold mt-4 text-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Verification</h1>
              <p className={`text-sm mt-2 text-center ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
                Please enter 6-digit pin sent on your<br />
                registered email address
              </p>
              <div className="p-6 text-center">
                <p className='mb-3'>Verification Code</p>
                <div className="flex justify-center space-x-3 mb-6">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(e.target, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      ref={(el) => (inputsRef.current[index] = el)}
                      className={`w-12 h-12 text-center text-xl font-bold rounded border outline-none focus:ring-2 ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-400'
                        : 'bg-white border-gray-300 text-black focus:ring-blue-600'
                        }`}
                    />
                  ))}
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={verifyOtpMutation.isLoading ? true : false}
                  className="w-[250px] poppins-regular bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-full">
                  {verifyOtpMutation.isLoading ? "Verifying...." : "Verify and Continue"}
                </button>
                <p className='mt-2 poppins-regular text-xs'>Didn’t receive the code? <span
                  onClick={() => resendOtp()}
                  className='font-bold cursor-pointer'>Resend</span></p>
              </div>
            </>
            :
            // {/* Register Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleRegister();
              }}>
              <div className="p-6">
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>Select one</label>
                  <div className="relative">
                    <select value={userType} onChange={(e) => setUserType(e.target.value)} className={`w-full mt-1 rounded-md shadow-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}`}>
                      {userTypes.map((type) => (
                        <option key={type?.value} value={type?.value}>{type?.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-6 flex flex-col items-center">
                  <div className={`w-16 h-16 border-2 border-dashed rounded-md flex items-center justify-center mb-2 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" />
                    ) : (
                      <Upload size={16} className="text-blue-600" />
                    )}
                    <input
                      type='file'
                      style={{ position: 'absolute', height: "65px", width: '65px', opacity: 0, cursor: 'pointer' }}
                      accept='image/*'
                      onChange={handleImageChange}
                    />
                  </div>
                  <span className={`text-sm text-gray-500 ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>Company logo/image</span>
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-600'} `}>
                    Company Name*
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder='Enter company name'
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className={`w-full p-3 rounded border ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300'
                        }`}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-600'} `}>
                    Email*
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      placeholder='Enter company email'
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full p-3 rounded border ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300'
                        }`}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <Mail size={18} className="text-blue-600" />
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-600'} `}>
                    Password*
                  </label>
                  <div className="relative">
                    <input
                      placeholder='********'
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full p-3 rounded border ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300'
                        }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3">
                      {showPassword ?
                        <EyeOff size={18} className="text-blue-600" /> :
                        <Eye size={18} className="text-blue-600" />
                      }
                    </button>
                  </div>
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-600'} `}>
                    Confirm Password*
                  </label>
                  <div className="relative">
                    <input
                      placeholder='********'
                      type={showConfPassword ? "text" : "password"}
                      value={confPassword}
                      onChange={(e) => setConfPassword(e.target.value)}
                      className={`w-full p-3 rounded border ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300'
                        }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfPassword(!showConfPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3">
                      {showConfPassword ?
                        <EyeOff size={18} className="text-blue-600" /> :
                        <Eye size={18} className="text-blue-600" />
                      }
                    </button>
                  </div>
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-600'} `}>
                    Contact number*
                  </label>
                  <div className="flex">
                    <PhoneInput
                      country={'us'}
                      value={countryCode + phoneNumber}
                      onChange={handlePhoneChange}
                      inputStyle={{
                        width: '100%',
                        backgroundColor: isDarkMode ? '#374151' : '#fff',
                        color: isDarkMode ? '#fff' : '#000',
                        borderColor: isDarkMode ? '#4B5563' : '#D1D5DB',
                        borderRadius: '0.375rem',
                        padding: '22px 50px',
                        fontSize: '1rem',
                      }}
                      buttonStyle={{
                        backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
                        borderColor: isDarkMode ? '#4B5563' : '#D1D5DB',
                      }}
                      containerStyle={{
                        width: '100%',
                        height: '45px'
                      }}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-600'} `}>
                    Address*
                  </label>
                  <AddressAutocomplete
                    placeholder={"Enter address"}
                    value={selectedAddress}
                    onSelect={(val) => setSelectedAddress(val)}
                  />
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-600'} `}>
                    Tax Identification number
                  </label>
                  <input
                    placeholder='XXXXX'
                    type="text"
                    value={taxId}
                    onChange={(e) => setTaxId(e.target.value)}
                    className={`w-full p-3 rounded border ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300'
                      }`}
                  />
                </div>
                <div className="mb-6">
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-600'} `}>
                    Organization Director/Share Holder ID*
                  </label>
                  <input
                    type="file"
                    accept=".pdf,image/*"
                    onChange={(e) => setCompanyDocument(e.target.files[0])}
                    className={`w-full p-2 rounded border ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300'
                      }`}
                  />
                </div>
                <div className='text-center'>
                  <button
                    type='submit'
                    disabled={RegisterMutation?.isLoading ? true : false}
                    className="w-[200px] poppins-regular bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-full focus:outline-none focus:shadow-outline">
                    {RegisterMutation.isLoading ? "Please Wait...." : "Sign Up"}
                  </button>
                </div>
              </div>
            </form>
          }
          {/* Footer Link */}
          <div className={`p-4 text-center ${isDarkMode ? 'bg-gray-700' : 'bg-blue-100'}`}>
            <p className={`${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
              Already have an account?
              <button
                onClick={() => navigate("/login")}
                className="ml-1 poppins-regular font-bold text-blue-600 hover:underline">
                Login
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
export default Register